import { TransportErrors } from './enums/errors';
import { Method } from './enums/method';
import { StatusCode } from './enums/status_code';
import { LoggingError } from './error/logging';
const DEFAULT_MELODY_ENDPOINT = 'https://@webgate/melody';
const LogValues = {
    UNKNOWN: 'unknown',
    EMPTY: '',
    ZERO: 0,
    EMPTY_VERSION: '0.0.0',
};
const LogEndpoints = {
    // Logging
    CLIENT_EVENT: {
        url: '/v1/logging/client_event',
    },
    ELAPSED_TIME: {
        url: '/v1/logging/elapsed_time',
    },
    JSSDK_ERROR: {
        url: '/v1/logging/jssdk_error',
        allowUnauthenticated: true,
    },
    JSSDK_INTERVAL_TIMER: {
        url: '/v1/logging/jssdk_interval_timer',
    },
    JSSDK_PLAYBACK_STATS: {
        url: '/v1/logging/jssdk_playback_stats',
        allowUnauthenticated: true,
    },
    JSSDK_PLAYBACK_START: {
        url: '/v1/logging/jssdk_playback_start',
    },
    JSSDK_STALL: {
        url: '/v1/logging/jssdk_stall',
        allowUnauthenticated: true,
    },
    JSSDK_TIMER: {
        url: '/v1/logging/jssdk_timer',
    },
    JSSDK_WARNING: {
        url: '/v1/logging/jssdk_warning',
        allowUnauthenticated: true,
    },
    TRACK_STREAM_VERIFICATION: {
        url: '/v1/logging/track_stream_verification',
    },
    // Metrics
    METRICS: {
        url: '/v1/metric',
    },
};
export class Logger {
    constructor(options) {
        /**
         * The ID for the sdk that will be filled up by promises.
         */
        this._sdkId = null;
        /**
         * The ID for the platform that will be filled up by promises.
         */
        this._platformId = null;
        /**
         * The version string for the client.
         */
        this._clientVersion = null;
        this._transport = options.transport;
        this._endpoint = options.endpoint || DEFAULT_MELODY_ENDPOINT;
        this._sdkIdPromise = Promise.resolve(options.sdkId || 'transport:1.0.0');
        this._platformPromise = Promise.resolve(options.platform || 'spotify-transport');
        this._clientVersionPromise = Promise.resolve(options.clientVersion || '0.0.0');
        this._allowUnauthenticated = !!options.allowUnauthenticated;
    }
    /**
     * Returns a new Logger Instance.
     *
     * @param options - The Logger options.
     * @return The logger instance.
     */
    static create(options) {
        return new Logger(options);
    }
    /**
     * Returns the ident data for the Logger instance.
     *
     * @return The ident data.
     */
    _getIdentData() {
        if (this._platformId && this._sdkId && this._clientVersion) {
            return Promise.resolve({
                platform: this._platformId,
                sdk_id: this._sdkId,
                client_version: this._clientVersion,
            });
        }
        return Promise.all([
            this._platformPromise,
            this._sdkIdPromise,
            this._clientVersionPromise,
        ]).then(([platform, sdkId, clientVersion]) => {
            this._platformId = platform;
            this._sdkId = sdkId;
            this._clientVersion = clientVersion;
            return Promise.resolve({
                platform: platform,
                sdk_id: sdkId,
                client_version: clientVersion,
            });
        });
    }
    /**
     * Decorates the payload to be sent with the platform and sdk_id ids.
     *
     * @param payload - The payload object.
     * @return The decorated payload.
     */
    _decoratePayload(payload) {
        return this._getIdentData().then(function (identData) {
            // Decorate
            payload.sdk_id = identData.sdk_id;
            payload.platform = identData.platform;
            payload.client_version = identData.client_version;
            return payload;
        });
    }
    /**
     * Sends a log message to the service.
     *
     * @param endpoint - The endpoint for the particular message.
     * @param payload - The payload to send.
     * @return True if the log was accepted by the server.
     */
    _sendLog(endpoint, payload) {
        const authorize = this._allowUnauthenticated && endpoint.allowUnauthenticated
            ? this._transport.isAuthenticated()
            : true;
        return this._transport
            .request(this._endpoint + endpoint.url, {
            method: Method.POST,
            authorize: authorize,
            payload: JSON.stringify(payload),
            retry: {
                maxRetries: 5,
                condition: function (response) {
                    return response.status !== StatusCode.ACCEPTED;
                },
            },
        })
            .then((response) => {
            const status = response.status;
            if (status !== StatusCode.ACCEPTED) {
                return Promise.reject(new LoggingError(TransportErrors.LOGGING_REQUEST_FAILED, `Logging service responded with status ${status}`, status));
            }
            return true;
        });
    }
    /**
     * Logs either a JSSDKError or a JSSDKWarning message, based on the endpoint.
     *
     * @param endpoint - The endpoint to log to.
     * @param data - The data to log.
     * @param jsonDataType - When set to 'json', the `data.json_data` value will
     *   be passed *as is* to the backend service. When set to 'raw', the value
     *   will be JSON serialized (via `JSON.stringify`) before being set to the
     *   backend. Set to 'raw' by default.
     * @return True if the log was accepted by the server.
     */
    _logErrorVariant(endpoint, data, jsonDataType) {
        const payload = {
            sdk_id: undefined,
            platform: undefined,
            client_version: undefined,
            source: data.source || LogValues.UNKNOWN,
            source_version: data.source_version || LogValues.UNKNOWN,
            type: data.type || LogValues.UNKNOWN,
            message: data.message || LogValues.EMPTY,
            stack: JSON.stringify(data.stack || LogValues.EMPTY),
            json_data: jsonDataType === 'json'
                ? data.json_data || '{}'
                : JSON.stringify(data.json_data || {}),
            json_data_version: data.json_data_version || LogValues.EMPTY_VERSION,
        };
        return this._decoratePayload(payload).then(this._sendLog.bind(this, endpoint));
    }
    /**
     * Logs a ClientEvent message to the backend.
     *
     * Note that this method could only guarantee that the message was sent to the
     * backend, not that it was delivered to the logging service.
     *
     * @param data - The data to log.
     * @param jsonDataType - When set to 'json', the `data.json_data` value will
     *   be passed *as is* to the backend service. When set to 'raw', the value
     *   will be JSON serialized (via `JSON.stringify`) before being set to the
     *   backend. Set to 'raw' by default.
     * @return True if the log was accepted by the server.
     */
    logClientEvent(data, jsonDataType) {
        if (!data) {
            return Promise.reject(new TypeError('Logger.logClientEvent `data` cannot be null.'));
        }
        const payload = {
            sdk_id: undefined,
            platform: undefined,
            client_version: undefined,
            source: data.source || LogValues.UNKNOWN,
            context: data.context || LogValues.UNKNOWN,
            event: data.event || LogValues.UNKNOWN,
            event_version: data.event_version || LogValues.EMPTY,
            test_version: data.test_version || LogValues.EMPTY,
            source_version: data.source_version || LogValues.UNKNOWN,
            source_vendor: data.source_vendor || LogValues.UNKNOWN,
            json_data: jsonDataType === 'json'
                ? data.json_data || '{}'
                : JSON.stringify(data.json_data || {}),
        };
        return this._decoratePayload(payload).then(this._sendLog.bind(this, LogEndpoints.CLIENT_EVENT));
    }
    /**
     * Logs a JSSDKError message.
     *
     * @param data - The data to log.
     * @param jsonDataType - When set to 'json', the `data.json_data` value
     *     will be passed *as is* to the backend service. When set to 'raw', the
     *     value will be JSON serialized (via `JSON.stringify`) before being set to
     *     the backend. Set to 'raw' by default.
     * @return True if the log was accepted by the server.
     */
    logJSSDKError(data, jsonDataType) {
        return this._logErrorVariant(LogEndpoints.JSSDK_ERROR, data, jsonDataType);
    }
    /**
     * Logs a JSSDKWarning message.
     *
     * @param data - The data to log.
     * @param jsonDataType - When set to 'json', the `data.json_data` value
     *     will be passed *as is* to the backend service. When set to 'raw', the
     *     value will be JSON serialized (via `JSON.stringify`) before being set to
     *     the backend. Set to 'raw' by default.
     * @return True if the log was accepted by the server.
     */
    logJSSDKWarning(data, jsonDataType) {
        return this._logErrorVariant(LogEndpoints.JSSDK_WARNING, data, jsonDataType);
    }
    logJSSDKPlaybackStats(data) {
        const payload = {
            sdk_id: undefined,
            platform: undefined,
            client_version: undefined,
            play_track: data.play_track,
            file_id: data.file_id,
            playback_id: data.playback_id,
            internal_play_id: data.internal_play_id,
            memory_cached: data.memory_cached,
            persistent_cached: data.persistent_cached,
            audio_format: data.audio_format,
            video_format: data.video_format,
            manifest_id: data.manifest_id,
            protected: data.protected,
            key_system: data.key_system,
            key_system_impl: data.key_system_impl,
            urls_json: data.urls_json,
            start_time: data.start_time,
            end_time: data.end_time,
            external_start_time: data.external_start_time,
            ms_play_latency: data.ms_play_latency,
            ms_init_latency: data.ms_init_latency,
            ms_head_latency: data.ms_head_latency,
            ms_manifest_latency: data.ms_manifest_latency,
            ms_resolve_latency: data.ms_resolve_latency,
            ms_license_generation_latency: data.ms_license_generation_latency,
            ms_license_request_latency: data.ms_license_request_latency,
            ms_license_session_latency: data.ms_license_session_latency,
            ms_license_update_latency: data.ms_license_update_latency,
            ms_played: data.ms_played,
            ms_nominal_played: data.ms_nominal_played,
            ms_file_duration: data.ms_file_duration,
            ms_actual_duration: data.ms_actual_duration,
            ms_start_position: data.ms_start_position,
            ms_end_position: data.ms_end_position,
            ms_seek_rebuffer: data.ms_seek_rebuffer,
            ms_seek_rebuffer_longest: data.ms_seek_rebuffer_longest,
            ms_stall_rebuffer: data.ms_stall_rebuffer,
            ms_stall_rebuffer_longest: data.ms_stall_rebuffer_longest,
            n_stalls: data.n_stalls,
            n_seekback: data.n_seekback,
            n_seekforward: data.n_seekforward,
            start_bitrate: data.start_bitrate,
            time_weighted_bitrate: data.time_weighted_bitrate,
            reason_start: data.reason_start,
            reason_end: data.reason_end,
            initially_paused: data.initially_paused,
            had_error: data.had_error,
            n_warnings: data.n_warnings,
            n_navigator_offline: data.n_navigator_offline,
            session_id: data.session_id,
            sequence_id: data.sequence_id,
        };
        return this._decoratePayload(payload).then(this._sendLog.bind(this, LogEndpoints.JSSDK_PLAYBACK_STATS));
    }
    logJSSDKPlaybackStart(data) {
        const payload = {
            sdk_id: undefined,
            platform: undefined,
            client_version: undefined,
            play_track: data.play_track,
            file_id: data.file_id,
            playback_id: data.playback_id,
            session_id: data.session_id,
            ms_start_position: data.ms_start_position,
            initially_paused: data.initially_paused,
        };
        return this._decoratePayload(payload).then(this._sendLog.bind(this, LogEndpoints.JSSDK_PLAYBACK_START));
    }
    logJSSDKStall(data) {
        const payload = {
            sdk_id: undefined,
            platform: undefined,
            client_version: undefined,
            session_id: data.session_id || LogValues.EMPTY,
            playback_id: data.playback_id || LogValues.EMPTY,
            play_track: data.play_track || LogValues.EMPTY,
            file_id: data.file_id || LogValues.EMPTY,
            byte_offset: data.byte_offset || LogValues.ZERO,
            time_offset: data.time_offset || LogValues.ZERO,
            ms_stalled: data.ms_stalled || LogValues.ZERO,
            timed_out: !!data.timed_out,
        };
        return this._decoratePayload(payload).then(this._sendLog.bind(this, LogEndpoints.JSSDK_STALL));
    }
    logTrackStreamVerification(data) {
        const payload = {
            sdk_id: undefined,
            platform: undefined,
            client_version: undefined,
            play_track: data.play_track,
            playback_id: data.playback_id,
            ms_played: data.ms_played,
            session_id: data.session_id,
            sequence_id: data.sequence_id,
            next_playback_id: data.next_playback_id,
        };
        return this._decoratePayload(payload).then(this._sendLog.bind(this, LogEndpoints.TRACK_STREAM_VERIFICATION));
    }
    logMetrics(meters, timers) {
        if (!meters && !timers) {
            return Promise.resolve(true);
        }
        const _meters = meters || [];
        const _timers = timers || [];
        if (!_meters.length && !_timers.length) {
            return Promise.resolve(true);
        }
        if (!Array.isArray(_meters) || !Array.isArray(_timers)) {
            return Promise.reject(new TypeError('Logger.logMetrics `meters` and `timers` must be null or an array.'));
        }
        for (const meter of _meters) {
            if (!('what' in meter) || !('result' in meter) || !('reason' in meter)) {
                return Promise.reject(new TypeError('Logger.logMetrics: Invalid meter format in `meters` array.'));
            }
        }
        // Check timers
        for (const timer of _timers) {
            if (!('what' in timer) || !('duration' in timer)) {
                return Promise.reject(new TypeError('Logger.logMetrics: Invalid timer format in `timers` array.'));
            }
        }
        const payload = {
            sdk_id: undefined,
            platform: undefined,
            client_version: undefined,
            meters: _meters,
            timers: _timers,
        };
        return this._decoratePayload(payload).then(this._sendLog.bind(this, LogEndpoints.METRICS));
    }
}
//# sourceMappingURL=logger.js.map